// src/components/financials/FundConfiguration.js
// Updated: 01/25/2026 - Starting Balance is now strictly "Create Only"

import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Switch, Chip, Alert, InputAdornment, Tooltip
} from '@mui/material';
import { Edit, Add, Archive, Unarchive, Lock } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, onSnapshot, doc, writeBatch, serverTimestamp, increment } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const FundConfiguration = () => {
  const { userProfile, currentUser } = useAuth();
  const churchId = userProfile?.churchId;

  const [funds, setFunds] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingFund, setEditingFund] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    startingBalance: 0, 
    isRestricted: false,
    isTaxDeductible: true,
    status: 'active'
  });

  useEffect(() => {
    if (!churchId) return;
    const ref = collection(db, 'churches', churchId, 'financials', 'data', 'funds');
    const unsubscribe = onSnapshot(ref, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFunds(data);
    });
    return () => unsubscribe();
  }, [churchId]);

  const handleOpen = (fund = null) => {
    if (fund) {
      setEditingFund(fund);
      setFormData({
        name: fund.name,
        startingBalance: fund.startingBalance || 0,
        isRestricted: fund.isRestricted || false,
        isTaxDeductible: fund.isTaxDeductible ?? true,
        status: fund.status || 'active'
      });
    } else {
      setEditingFund(null);
      setFormData({ name: '', startingBalance: 0, isRestricted: false, isTaxDeductible: true, status: 'active' });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    if (!churchId) return;
    setLoading(true);

    try {
      const batch = writeBatch(db);
      const newStartingBalance = parseFloat(formData.startingBalance) || 0;
      
      const payload = {
        name: formData.name,
        isRestricted: formData.isRestricted,
        isTaxDeductible: formData.isTaxDeductible,
        status: formData.status,
        updatedAt: serverTimestamp()
      };

      let fundRef;
      let difference = 0;
      let isNew = false;

      if (editingFund) {
        // --- EDIT MODE ---
        // Logic: Never touch startingBalance during an edit.
        fundRef = doc(db, 'churches', churchId, 'financials', 'data', 'funds', editingFund.id);
        
        // Ensure payload preserves the original starting balance purely for record-keeping
        payload.startingBalance = editingFund.startingBalance || 0;
        
        difference = 0; // No financial adjustments allowed during edit
        batch.update(fundRef, payload);

      } else {
        // --- CREATE MODE ---
        isNew = true;
        fundRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
        
        // Logic: Allow setting startingBalance ONLY here
        difference = newStartingBalance; 
        payload.startingBalance = newStartingBalance;

        batch.set(fundRef, {
            ...payload,
            createdAt: serverTimestamp(),
            balance: 0,        
            currentBalance: 0 
        });
      }

      // --- CREATE TRANSACTION & UPDATE BALANCE (Only for New Funds) ---
      if (difference !== 0) {
          const txRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'transactions'));
          
          const journalEntry = {
              batchId: 'opening_balance',
              date: new Date().toISOString().split('T')[0],
              status: 'posted',
              reconciled: true,
              reconciledAt: serverTimestamp(),
              reconciledBy: currentUser.uid,
              type: 'opening_balance', 
              method: 'System Adjustment',
              donorName: 'Opening Balance Equity',
              note: `Initial Opening Balance for ${formData.name}`,
              amount: difference,
              fundId: fundRef.id,
              splits: [{
                  fundId: fundRef.id,
                  fundName: formData.name,
                  amount: difference
              }],
              createdAt: serverTimestamp(),
              createdBy: currentUser.uid
          };

          batch.set(txRef, journalEntry);

          if (editingFund) {
              batch.update(fundRef, { 
                  balance: increment(difference),
                  currentBalance: increment(difference)
              });
          } else {
              batch.update(fundRef, {
                  balance: increment(difference),
                  currentBalance: increment(difference)
              });
          }
      }

      await batch.commit();
      handleClose();

    } catch (error) {
      console.error("Error saving fund:", error);
      alert("Failed to save fund: " + error.message);
    }
    setLoading(false);
  };

  const toggleArchive = async (fund) => {
    if (!window.confirm(`Are you sure you want to ${fund.status === 'active' ? 'archive' : 'activate'} this fund?`)) return;
    try {
      const docRef = doc(db, 'churches', churchId, 'financials', 'data', 'funds', fund.id);
      await writeBatch(db).update(docRef, { 
        status: fund.status === 'active' ? 'archived' : 'active' 
      }).commit();
    } catch (e) {
      console.error(e);
    }
  };

  // --- UPDATED LOCK LOGIC: Always locked if editing ---
  const isLocked = !!editingFund;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Chart of Accounts (Funds)</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
          Create New Fund
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fund Name</TableCell>
            <TableCell align="right">Starting Balance</TableCell> 
            <TableCell>Attributes</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Current Balance</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {funds.sort((a,b) => (a.name || "").localeCompare(b.name || "")).map((fund) => (
            <TableRow key={fund.id} sx={{ opacity: fund.status === 'archived' ? 0.6 : 1 }}>
              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                {fund.name}
              </TableCell>
              <TableCell align="right">
                 ${(fund.startingBalance || 0).toLocaleString()}
              </TableCell>
              <TableCell>
                {fund.isRestricted && <Chip label="Restricted" size="small" color="warning" sx={{ mr: 1 }} />}
                {!fund.isTaxDeductible && <Chip label="Non-Deductible" size="small" variant="outlined" />}
              </TableCell>
              <TableCell>
                <Chip 
                  label={fund.status === 'active' ? 'Active' : 'Archived'} 
                  color={fund.status === 'active' ? 'success' : 'default'} 
                  size="small" 
                />
              </TableCell>
              <TableCell>
                ${(fund.balance || fund.currentBalance || 0).toLocaleString()}
              </TableCell>
              <TableCell align="right">
                <IconButton onClick={() => handleOpen(fund)} size="small">
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton onClick={() => toggleArchive(fund)} size="small" color={fund.status === 'active' ? 'default' : 'success'}>
                  {fund.status === 'active' ? <Archive fontSize="small" /> : <Unarchive fontSize="small" />}
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {funds.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No funds configured. Click "Create New Fund" to start.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFund ? 'Edit Fund' : 'New Fund'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Fund Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="e.g. General Fund"
          />
          
          <TextField
            margin="dense"
            label="Starting Balance ($)"
            type="number"
            fullWidth
            InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                endAdornment: isLocked ? <Lock color="action" /> : null
            }}
            value={formData.startingBalance}
            onChange={(e) => setFormData({...formData, startingBalance: e.target.value})}
            // --- THE LOCK LOGIC ---
            disabled={isLocked}
            helperText={isLocked 
                ? "Locked: Use a Journal Entry to adjust balances after creation." 
                : "Enter the money you ALREADY have in the bank for this fund."}
          />

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch 
                  checked={formData.isRestricted} 
                  onChange={(e) => setFormData({...formData, isRestricted: e.target.checked})} 
                />
              }
              label="Restricted Fund"
            />
             <FormControlLabel
              control={
                <Switch 
                  checked={formData.isTaxDeductible} 
                  onChange={(e) => setFormData({...formData, isTaxDeductible: e.target.checked})} 
                />
              }
              label="Tax Deductible"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={loading || !formData.name}>
            Save Fund
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundConfiguration;